import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';
import Button from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const handleKeyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(element => !element.hasAttribute('disabled'));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyboard);
    return () => {
      document.removeEventListener('keydown', handleKeyboard);
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/60 p-4 backdrop-blur-sm'
      onMouseDown={event => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={clsx(
          'max-h-[90vh] w-full overflow-y-auto rounded-2xl border border-secondary-200 bg-white shadow-2xl dark:border-secondary-700 dark:bg-secondary-900',
          sizeClasses[size]
        )}
      >
        <div className='flex items-start gap-4 border-b border-secondary-200 px-5 py-4 dark:border-secondary-800'>
          <div className='min-w-0 flex-1'>
            <h2
              id={titleId}
              className='text-lg font-bold text-secondary-950 dark:text-white'
            >
              {title}
            </h2>
            {description ? (
              <p
                id={descriptionId}
                className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'
              >
                {description}
              </p>
            ) : null}
          </div>
          <Button
            ref={closeButtonRef}
            variant='ghost'
            size='sm'
            className='h-9 w-9 px-0'
            onClick={onClose}
            aria-label='إغلاق النافذة'
          >
            <X className='h-5 w-5' />
          </Button>
        </div>
        <div className='p-5'>{children}</div>
        {footer ? (
          <div className='flex flex-wrap justify-end gap-2 border-t border-secondary-200 px-5 py-4 dark:border-secondary-800'>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Modal;
